import React, { useState, useRef, useEffect } from 'react';
import { RiSettings2Line } from 'react-icons/ri';
import json5 from 'json5'; // more forgiving json parser
import { OrderingOperations } from 'polygon-ordering';
import { promptForDevEndpoint } from '../utils/endpoint';
import { setThemeOverrides } from '../slices/config/themeOverrides';
import { setEnableThemeKeyHints } from '../slices/config/enableThemeKeyHints';
import { applyServerThemeOverrides } from '../thunks/applyServerThemeOverrides';
import TouchableOpacity from './TouchableOpacity';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { logEvent } from '../utils/analytics';
import EVENTS from '../constants/events';
import { setOrderDate } from '../slices/orderDate';
import defaultTheme from '../constants/theme';
import getThemeLookup from '../selectors/getThemeLookup';
import lodash from 'lodash';
import Editor from 'react-simple-code-editor';
//@ts-ignore
import { UilMultiply, UilSave, UilCopy } from '@iconscout/react-unicons';

const SIZE = 40;

function fallbackCopyTextToClipboard(text: string) {
  var textArea = document.createElement('textarea');
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  document.body.removeChild(textArea);
}

function copyTextToClipboard(text: string) {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
    return;
  }
  navigator.clipboard.writeText(text);
}

const DevTools: React.FC = () => {
  const { startNewOrder, setShowHiddenLocations } = OrderingOperations;
  const [state, setState] = useState<{ showThemeTool: boolean; value: string }>({
    showThemeTool: false,
    value: '',
  });
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dispatch = useAppDispatch();
  const enableThemeKeyHints = useAppSelector(state => state.config.enableThemeKeyHints);
  const showHiddenLocations = useAppSelector(state => state.ordering.config.showHiddenLocations);
  const enableThemeTools = useAppSelector(state => state.config.enableThemeTools);

  const loadValueFromStorage = () => {
    setState({ ...state, value: localStorage.getItem('orderingDevThemeOverrides') || '' });
  };

  const p = useAppSelector(getThemeLookup);

  const highlightHexCodes = (code: string) => {
    const hexRegex = /#([0-9A-Fa-f]{3}){1,2}\b/g; // Regex to find hex codes
    return code.replace(hexRegex, match => {
      return `<span style="background-color: ${match}; padding: 0 0px; border-radius: 3px"><span style="color: #fff;mix-blend-mode: exclusion; ">${match}</span></span>`;
    });
  };

  const loadValueFromStorageAndMigrate = () => {
    const currentThemeRaw = localStorage.getItem('orderingDevThemeOverrides') || '';
    setState({ ...state, value: currentThemeRaw || '' });

    // Determine the constants
    const determinedConstants: ThemeConstantsRaw = {
      color: {
        primary:
          p('check', ['color']).color ?? p('defaultButton', ['backgroundColor']).backgroundColor,
        onPrimary: p('choiceChecked', ['color']).color ?? p('defaultButton', ['color']).color,

        onSurface: p('defaultText', ['color']).color,
        //outline: 'OUTLINE',
        //outlineVariant: 'OUTLINE_VARIANT',

        surface: p('screen', ['backgroundColor']).backgroundColor,
        //surfaceVariant: 'LIGHTESTGREY',
      },
    };

    // Pass determined constants to defaultTheme
    const defaultThemeWithConstants: ThemeOverrides = defaultTheme(determinedConstants);

    // "Treeshaking" - Check every currentTheme property's keys compare it with defaultTheme,
    // if they are the same remove it.
    let currentTheme: ThemeOverrides = JSON.parse(currentThemeRaw);

    // Loop through each property in currentTheme
    for (const themeProp in currentTheme) {
      // Check if the theme property exists in defaultThemeWithConstants and if it's an object
      if (
        Object.prototype.hasOwnProperty.call(defaultThemeWithConstants, themeProp) &&
        typeof currentTheme[themeProp] === 'object' &&
        currentTheme[themeProp] !== null
      ) {
        for (const themeKey in currentTheme[themeProp]) {
          // Check if the theme key exists in defaultThemeWithConstants[themeProp] and both values are of the same type
          if (
            Object.prototype.hasOwnProperty.call(defaultThemeWithConstants[themeProp], themeKey) &&
            typeof currentTheme[themeProp][themeKey] ===
              typeof defaultThemeWithConstants[themeProp][themeKey]
          ) {
            // If the values match, delete the theme key from currentTheme[key]
            if (
              currentTheme[themeProp][themeKey] === defaultThemeWithConstants[themeProp][themeKey]
            ) {
              delete currentTheme[themeProp][themeKey];
            }
          }
        }
      }
    }

    // Remove nolonger needed keys, that would messup constants
    const erroneousThemeKeys = [
      'color.check',
      'color.quantityPickerIcon',
      'backgroundColor.quantityPickerIcon',
      'backgroundColor.purchaseEditorRight',
      'backgroundColor.purchaseEditorLeft',
      'backgroundColor.itemChoiceActiveGrid',
    ];

    erroneousThemeKeys.forEach(themeKey => {
      lodash.unset(currentTheme, themeKey);
    });

    //@ts-ignore
    currentTheme = lodash.chain({ constants: determinedConstants }).merge(currentTheme);

    const newThemeOverrides = JSON.stringify(currentTheme, null, 2);

    setState({ ...state, value: newThemeOverrides });
  };

  useEffect(() => {
    loadValueFromStorage();
  }, []);

  const changeThemeValue = (value: string) => {
    localStorage.setItem('orderingDevThemeOverrides', value);
    setState({ ...state, value });
  };

  const { showThemeTool, value } = state;
  const themeToolRef = useRef<any>(showThemeTool);

  useEffect(() => {
    if (!themeToolRef.current && showThemeTool) {
      inputRef.current?.focus();
    }
    themeToolRef.current = showThemeTool;
  }, [showThemeTool]);

  const close = () => setState({ ...state, showThemeTool: false });

  const apply = () => {
    if (!value) {
      dispatch(setThemeOverrides({}));
      return;
    }

    try {
      const themeOverrides = json5.parse(value);
      dispatch(setThemeOverrides(themeOverrides));
      changeThemeValue(JSON.stringify(themeOverrides, null, 2));
    } catch {
      alert('syntax error');
      return;
    }
  };

  return (
    <>
      <TouchableOpacity
        style={styles.main}
        onClick={() => setState({ ...state, showThemeTool: !showThemeTool })}
      >
        <RiSettings2Line color="white" size={SIZE - 15} />
      </TouchableOpacity>

      {showThemeTool && (
        <div
          style={styles.overlay}
          onClick={event => {
            if (event.target === event.currentTarget) {
              setState({ ...state, showThemeTool: !showThemeTool });
            }
          }}
        >
          <div style={styles.modal}>
            <div style={styles.buttons}>
              <TouchableOpacity onClick={close} style={styles.button}>
                <UilMultiply />
              </TouchableOpacity>

              <TouchableOpacity onClick={apply} style={styles.button}>
                <UilSave />
              </TouchableOpacity>

              <TouchableOpacity onClick={() => copyTextToClipboard(value)} style={styles.button}>
                <UilCopy />
              </TouchableOpacity>

              <TouchableOpacity
                onClick={() => {
                  changeThemeValue('');
                  dispatch(setThemeOverrides({}));
                }}
                style={styles.button}
              >
                Clear Theme
              </TouchableOpacity>

              <TouchableOpacity
                onClick={() => {
                  dispatch(applyServerThemeOverrides());
                  setTimeout(() => loadValueFromStorage(), 1000);
                }}
                style={styles.button}
              >
                Load PC Theme
              </TouchableOpacity>

              <TouchableOpacity
                onClick={() => {
                  dispatch(applyServerThemeOverrides());
                  setTimeout(() => loadValueFromStorageAndMigrate(), 1000);
                }}
                style={styles.button}
              >
                Migrate
              </TouchableOpacity>

              {!enableThemeTools && (
                <TouchableOpacity onClick={() => promptForDevEndpoint()} style={styles.button}>
                  Change Endpoint
                </TouchableOpacity>
              )}

              <TouchableOpacity
                onClick={() => {
                  dispatch(setOrderDate(null));
                  dispatch(startNewOrder({ preserveMember: true })).then(() => {
                    logEvent(EVENTS.START_NEW_ORDER);
                  });
                }}
                style={styles.button}
              >
                New Order
              </TouchableOpacity>

              <TouchableOpacity onClick={() => window._showStringOverrides()} style={styles.button}>
                Show String Keys
              </TouchableOpacity>

              {!enableThemeTools && (
                <TouchableOpacity
                  onClick={() => dispatch(setShowHiddenLocations(!showHiddenLocations))}
                  style={styles.button}
                >
                  {showHiddenLocations ? 'Show Hidden: T' : 'Show Hidden: F'}
                </TouchableOpacity>
              )}

              <TouchableOpacity
                onClick={() => dispatch(setEnableThemeKeyHints(!enableThemeKeyHints))}
                style={styles.button}
              >
                {enableThemeKeyHints ? 'Theme Hints: T' : 'Theme Hints: F'}
              </TouchableOpacity>
            </div>
            <div style={styles.codeContainer}>
              <Editor
                value={value}
                onValueChange={newCode => changeThemeValue(newCode)}
                highlight={code => highlightHexCodes(code)}
                padding={10}
                placeholder="Enter theme overrides"
                style={styles.codeEditor}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const styles: Styles = {
  main: {
    position: 'absolute',

    bottom: 10,
    left: 10,

    height: SIZE,
    width: SIZE,

    borderRadius: SIZE / 2,

    backgroundColor: 'black',

    zIndex: 999999,

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  overlay: {
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 999998,
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    padding: 60,
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'space-between',
  },
  modal: {
    display: 'flex',
    borderRadius: 15,
    overflow: 'clip',
    flexDirection: 'column',
    flex: 1,
    height: '100%',
  },
  codeContainer: {
    overflowY: 'scroll',
    height: '100%',
    borderTop: '1px solid #DFDFDF',
  },
  codeEditor: {
    fontFamily: 'monospace',
    fontSize: 14,
    background: '#fff',
    color: '#000',
    border: 'none',
    whiteSpace: 'pre-wrap',
    minHeight: '100%',
  },
  buttons: {
    width: '100%',
    height: 70,
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    overflowX: 'auto',
    overflowY: 'hidden',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  button: {
    color: '#737373',
    marginLeft: 15,
    marginRight: 15,
  },
};

export default DevTools;
